/** Centralized API client with retry logic. */

import { API_BASE_URL, API_KEY, RETRY_CONFIG } from '@/utils/constants';
import type { ApiError } from '@/types/api';

export interface ApiClientResponse<T> {
  data: T;
  status: number;
}

/** Result of a conditional GET (ETag). */
export type ConditionalGetResult<T> =
  | { notModified: true; etag?: string }
  | { notModified: false; data: T; etag?: string };

export class ApiClient {
  private baseUrl: string;
  private maxRetries: number;
  private baseDelay: number;
  private defaultTimeoutMs: number;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.maxRetries = RETRY_CONFIG.maxRetries;
    this.baseDelay = RETRY_CONFIG.baseDelay;
    this.defaultTimeoutMs = 30000;
  }

  private async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    retries: number = this.maxRetries
  ): Promise<Response> {
    let lastError: Error | null = null;

    // Add API key to headers if configured
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };
    if (API_KEY) {
      headers['X-API-Key'] = API_KEY;
    }

    for (let attempt = 0; attempt < retries; attempt++) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), this.defaultTimeoutMs);
      try {
        const response = await fetch(url, {
          ...options,
          headers,
          signal: options.signal ?? controller.signal,
        });
        window.clearTimeout(timeout);

        // Retry on 5xx errors or network errors
        if (response.ok || (response.status < 500 && response.status !== 429)) {
          return response;
        }

        // Don't retry on 4xx errors (client errors) except 429 (rate limit)
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // For 5xx or 429, retry with exponential backoff
        if (attempt < retries - 1) {
          const delay = this.baseDelay * Math.pow(2, attempt);
          console.warn(
            `Request failed (${response.status}), retrying in ${delay}ms... (attempt ${attempt + 1}/${retries})`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } catch (error) {
        window.clearTimeout(timeout);
        lastError = error instanceof Error ? error : new Error(String(error));

        // Avoid expensive retries for aborted/offline-style failures
        if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('Failed to fetch'))) {
          throw lastError;
        }

        // If this is the last attempt, throw the error
        if (attempt === retries - 1) {
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Failed to fetch');
  }

  async get<T>(url: string, retries?: number): Promise<ApiClientResponse<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const response = await this.fetchWithRetry(fullUrl, { method: 'GET' }, retries);
    
    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data, status: response.status };
  }

  /**
   * GET with optional If-None-Match. Returns notModified when server responds 304 (no body).
   */
  async getWithETag<T>(url: string, etag?: string | null): Promise<ConditionalGetResult<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const headers: Record<string, string> = {};
    if (API_KEY) {
      headers['X-API-Key'] = API_KEY;
    }
    if (etag) {
      headers['If-None-Match'] = etag;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.defaultTimeoutMs);
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
      window.clearTimeout(timeout);
      if (response.status === 304) {
        return { notModified: true, etag: etag ?? undefined };
      }
      if (!response.ok) {
        const errorData: ApiError = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      const nextEtag = response.headers.get('ETag') ?? undefined;
      const data = (await response.json()) as T;
      return { notModified: false, data, etag: nextEtag };
    } catch (e) {
      window.clearTimeout(timeout);
      throw e instanceof Error ? e : new Error(String(e));
    }
  }

  async post<T>(url: string, body: unknown, retries?: number): Promise<ApiClientResponse<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const response = await this.fetchWithRetry(
      fullUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
      retries
    );
    
    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data, status: response.status };
  }

  async delete<T>(url: string, retries?: number): Promise<ApiClientResponse<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const response = await this.fetchWithRetry(
      fullUrl,
      {
        method: 'DELETE',
      },
      retries
    );
    
    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { data, status: response.status };
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

